import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, updateDoc, increment, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { CartItem } from '../types/inventory';

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => Promise<boolean>;
  removeFromCart: (itemId: string, roomId: string) => Promise<void>;
  updateCartItemQuantity: (itemId: string, roomId: string, quantity: number) => Promise<boolean>;
  clearCart: () => Promise<void>;
  clearCartWithoutRelease: () => void;
  getTotalItems: () => number;
  isInCart: (itemId: string, roomId: string) => boolean;
  getCartItem: (itemId: string, roomId: string) => CartItem | undefined;
  // Cart drawer state
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'inventory_cart';

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    // Load cart from localStorage on initialization
    let savedCart: string | null = null;
    try {
      savedCart = localStorage.getItem(CART_STORAGE_KEY);
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
    
    // Handle the conditional logic outside of try/catch
    if (savedCart) {
      try {
        return JSON.parse(savedCart);
      } catch (error) {
        console.error('Error parsing cart data:', error);
        return [];
      }
    }
    
    return [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  const addToCart = async (item: CartItem): Promise<boolean> => {
    try {
      // First, check current inventory availability
      const itemRef = doc(db, 'inventoryItems', item.itemId);
      const itemDoc = await getDoc(itemRef);

      if (!itemDoc.exists()) {
        console.error('Item no longer exists');
        return false;
      }

      const currentData = itemDoc.data();
      const currentAvailableCount = currentData.availableCount || 0;

      // Validate we have enough inventory
      if (currentAvailableCount < item.quantity) {
        console.error(`Insufficient quantity. Available: ${currentAvailableCount}, Requested: ${item.quantity}`);
        return false;
      }

      // Reserve inventory by decreasing availableCount in Firestore
      await updateDoc(itemRef, {
        availableCount: increment(-item.quantity),
      });

      // Update local cart state
      setCartItems((prev) => {
        const existingIndex = prev.findIndex(
          (cartItem) => cartItem.itemId === item.itemId && cartItem.roomId === item.roomId
        );

        if (existingIndex >= 0) {
          // Update quantity of existing item
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: updated[existingIndex].quantity + item.quantity,
          };
          return updated;
        } else {
          // Add new item to cart
          return [...prev, item];
        }
      });

      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  };

  const removeFromCart = async (itemId: string, roomId: string): Promise<void> => {
    try {
      // Find the item in cart to get the quantity to release
      const cartItem = cartItems.find(
        (item) => item.itemId === itemId && item.roomId === roomId
      );

      if (!cartItem) {
        console.error('Item not found in cart');
        return;
      }

      // Release the reserved inventory back to Firestore
      const itemRef = doc(db, 'inventoryItems', itemId);
      await updateDoc(itemRef, {
        availableCount: increment(cartItem.quantity),
      });

      // Remove from local cart state
      setCartItems((prev) =>
        prev.filter((item) => !(item.itemId === itemId && item.roomId === roomId))
      );
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const updateCartItemQuantity = async (itemId: string, roomId: string, quantity: number): Promise<boolean> => {
    try {
      if (quantity <= 0) {
        await removeFromCart(itemId, roomId);
        return true;
      }

      // Find current cart item
      const cartItem = cartItems.find(
        (item) => item.itemId === itemId && item.roomId === roomId
      );

      if (!cartItem) {
        console.error('Item not found in cart');
        return false;
      }

      const currentCartQuantity = cartItem.quantity;
      const quantityDifference = quantity - currentCartQuantity;

      if (quantityDifference === 0) {
        return true; // No change needed
      }

      // Check current inventory availability if increasing
      if (quantityDifference > 0) {
        const itemRef = doc(db, 'inventoryItems', itemId);
        const itemDoc = await getDoc(itemRef);

        if (!itemDoc.exists()) {
          console.error('Item no longer exists');
          return false;
        }

        const currentData = itemDoc.data();
        const currentAvailableCount = currentData.availableCount || 0;

        if (currentAvailableCount < quantityDifference) {
          console.error(`Insufficient quantity. Available: ${currentAvailableCount}, Requested: ${quantityDifference}`);
          return false;
        }
      }

      // Update Firestore inventory (negative for increase, positive for decrease)
      const itemRef = doc(db, 'inventoryItems', itemId);
      await updateDoc(itemRef, {
        availableCount: increment(-quantityDifference),
      });

      // Update local cart state
      setCartItems((prev) => {
        const index = prev.findIndex(
          (item) => item.itemId === itemId && item.roomId === roomId
        );

        if (index >= 0) {
          const updated = [...prev];
          updated[index] = { ...updated[index], quantity };
          return updated;
        }
        return prev;
      });

      return true;
    } catch (error) {
      console.error('Error updating cart item quantity:', error);
      return false;
    }
  };

  const clearCart = async (): Promise<void> => {
    try {
      // Release all reserved inventory back to Firestore
      for (const item of cartItems) {
        const itemRef = doc(db, 'inventoryItems', item.itemId);
        await updateDoc(itemRef, {
          availableCount: increment(item.quantity),
        });
      }

      // Clear local cart state
      setCartItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const clearCartWithoutRelease = () => {
    // Clear cart without releasing inventory (used when order is placed)
    setCartItems([]);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const isInCart = (itemId: string, roomId: string) => {
    return cartItems.some((item) => item.itemId === itemId && item.roomId === roomId);
  };

  const getCartItem = (itemId: string, roomId: string) => {
    return cartItems.find((item) => item.itemId === itemId && item.roomId === roomId);
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const value: CartContextType = {
    cartItems,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    clearCartWithoutRelease,
    getTotalItems,
    isInCart,
    getCartItem,
    isCartOpen,
    openCart,
    closeCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
